/**
 * DesktopFolder Component
 * Zeigt einen Ordner auf dem Desktop mit Badge fuer App-Anzahl
 * Aehnlich wie DesktopIcon, aber mit Folder-Icon und Klick oeffnet Dialog
 */

import { memo, type ComponentProps, useEffect, useRef, useState } from 'react'
import { motion, useAnimation, useMotionValue, type PanInfo } from 'framer-motion'
import { ICON_SIZE } from '@/utils/desktop-layout-utils'
import type { GridPosition } from '@/hooks/useIconGridPosition'
import type { DesktopFolder as DesktopFolderData } from '@/services/folder-service'
import { APPS } from '@/data/apps'

type MotionDivProps = ComponentProps<typeof motion.div>
type DragConstraints = MotionDivProps['dragConstraints']

type DesktopFolderProps = {
  folderId: string
  folder: DesktopFolderData
  onOpen: () => void
  showIcon?: boolean
  showLabel?: boolean
  iconColor?: string
  textColor?: string
  wiggleTrigger?: number
  position?: { x: number; y: number }
  onDragEnd?: (position: GridPosition) => void
  pixelsToGrid?: (x: number, y: number) => GridPosition
  dragConstraints?: DragConstraints
  gridToPixels?: (position: GridPosition) => { x: number; y: number }
  isDropTarget?: boolean
}

function DesktopFolder({
  folderId,
  folder,
  onOpen,
  showIcon = true,
  showLabel = true,
  iconColor = '#F5A623',
  textColor = '#FFFFFF',
  wiggleTrigger = 0,
  position = { x: 0, y: 0 },
  onDragEnd,
  pixelsToGrid,
  dragConstraints,
  gridToPixels,
  isDropTarget = false,
}: DesktopFolderProps) {
  const iconControls = useAnimation()
  const [isDragging, setIsDragging] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [allowHover, setAllowHover] = useState(true)
  const dragStartPosRef = useRef(position)

  const motionX = useMotionValue(position.x)
  const motionY = useMotionValue(position.y)

  useEffect(() => {
    motionX.set(position.x)
    motionY.set(position.y)
  }, [position, motionX, motionY])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onOpen()
    }
  }

  const handleDragStart = () => {
    setIsDragging(true)
    dragStartPosRef.current = { x: position.x, y: position.y }
  }

  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    setIsDragging(false)

    if (onDragEnd && pixelsToGrid && gridToPixels) {
      const finalX = dragStartPosRef.current.x + info.offset.x
      const finalY = dragStartPosRef.current.y + info.offset.y
      const gridPos = pixelsToGrid(finalX, finalY)
      const targetPos = gridToPixels(gridPos)

      motionX.set(targetPos.x)
      motionY.set(targetPos.y)
      setIsHovered(false)
      setAllowHover(false)
      setTimeout(() => setAllowHover(true), 300)
      onDragEnd(gridPos)
    }
  }

  useEffect(() => {
    iconControls.set({ rotate: 0 })
  }, [iconControls])

  useEffect(() => {
    if (!wiggleTrigger) return
    iconControls.start({
      rotate: [0, -3, 3, -3, 0],
      transition: {
        duration: 0.5,
        ease: 'easeInOut',
      },
    })
  }, [wiggleTrigger, iconControls])

  const appCount = folder.appIds.length

  return (
    <motion.div
      className="group select-none flex flex-col items-center justify-start rounded-lg"
      style={{
        width: `${ICON_SIZE.width}px`,
        minHeight: `${ICON_SIZE.height}px`,
        paddingTop: '10px',
        position: 'absolute',
        left: 0,
        top: 0,
        cursor: isDragging ? 'grabbing' : 'pointer',
        x: motionX,
        y: motionY,
      }}
      role="button"
      aria-label={`${folder.name}, enthaelt ${appCount} Apps`}
      tabIndex={0}
      onClick={() => {
        if (!isDragging) {
          onOpen()
        }
      }}
      onKeyDown={onKeyDown}
      onMouseEnter={() => {
        if (!isDragging && allowHover) {
          setIsHovered(true)
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false)
      }}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{
        opacity: 1,
        scale: isHovered && !isDragging && allowHover ? 1.05 : isDropTarget ? 1.1 : 1,
        backgroundColor: isHovered && !isDragging && allowHover
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(255, 255, 255, 0)',
        boxShadow: isDropTarget
          ? `0 0 30px ${iconColor}, 0 0 60px ${iconColor}40`
          : '0 0 0 rgba(0, 0, 0, 0)',
      }}
      whileTap={{
        scale: 0.95,
        transition: { duration: 0.1 },
      }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
      }}
      drag
      dragMomentum={false}
      dragElastic={0}
      dragConstraints={dragConstraints}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      data-driver-id="desktop-folder"
      data-driver-folder-id={folderId}
    >
      {showIcon && (
        <div className="relative">
          <motion.div
            animate={iconControls}
            onHoverStart={() => {
              iconControls.start({
                rotate: [0, -3, 3, -3, 0],
                transition: {
                  duration: 0.5,
                  ease: 'easeInOut',
                },
              })
            }}
            transition={{
              duration: 0.2,
              ease: 'easeOut',
            }}
            data-driver-id="desktop-folder-inner"
            className="w-10 h-10 rounded-xl overflow-hidden relative"
            style={{
              backgroundColor: 'var(--taskbar-bg)',
              backdropFilter: 'blur(10px)',
              border: '2px solid',
              borderColor: iconColor,
              boxShadow: `0 0 15px ${iconColor}40`,
            }}
          >
            <div className="grid grid-cols-2 grid-rows-2 w-full h-full p-0.5 gap-0.5">
              {folder.appIds.slice(0, 4).map((appId, index) => {
                const app = APPS.find((a) => a.id === appId)
                if (!app) return <div key={index} className="w-full h-full" />

                const Icon = app.Icon
                return (
                  <div
                    key={appId}
                    className="w-full h-full flex items-center justify-center rounded-sm"
                    style={{
                      backgroundColor: 'var(--window-background)',
                    }}
                  >
                    <span style={{ color: iconColor }}><Icon className="w-3 h-3" /></span>
                  </div>
                )
              })}
              {Array.from({ length: Math.max(0, 4 - folder.appIds.length) }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="w-full h-full rounded-sm"
                  style={{
                    backgroundColor: 'var(--surface-secondary)',
                  }}
                />
              ))}
            </div>
          </motion.div>
        </div>
      )}

      {showLabel && (
        <div
          className="text-center w-full px-1 break-words"
          style={{
            marginTop: showIcon ? '4px' : '0px',
            textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
            fontSize: '11px',
            color: textColor,
            fontWeight: '500',
            lineHeight: '1.2',
            wordBreak: 'break-word',
          }}
          title={`${folder.name} (${appCount} Apps)`}
        >
          {folder.name}
        </div>
      )}
    </motion.div>
  )
}

export default memo(DesktopFolder)
